import React, { useState } from 'react';
import { Link, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  LayoutDashboard,
  Images,
  Package,
  ShoppingCart,
  LogOut,
  Menu,
  X,
  ChevronDown,
  User,
  Palette,
  Box,
  Send,
  Tag,
  FileText,
  Layers,
  Calendar,
  ClipboardList,
  BarChart3,
  MessageSquare,
  MailCheck,
  Settings
} from 'lucide-react';

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigation = [
    { name: 'Dashboard', href: '/admin', icon: LayoutDashboard },
    { name: 'Galleries', href: '/admin/galleries', icon: Images },
    { name: 'Products', href: '/admin/products', icon: Box },
    { name: 'Packages', href: '/admin/packages', icon: Package },
    { name: 'Templates', href: '/admin/templates', icon: Layers },
    { name: 'Pricing Sheets', href: '/admin/pricing-sheets', icon: FileText },
    { name: 'Backgrounds', href: '/admin/backgrounds', icon: Palette },
    { name: 'Discounts', href: '/admin/discounts', icon: Tag },
    { name: 'Orders', href: '/admin/orders', icon: ShoppingCart },
    { name: 'Pre-Order Events', href: '/admin/preorder-events', icon: Calendar },
    { name: 'Pre-Orders', href: '/admin/preorders', icon: ClipboardList },
    { name: 'Bulk Email', href: '/admin/bulk-email', icon: Send },
    { name: 'Responses', href: '/admin/responses', icon: MailCheck },
    { name: 'SMS', href: '/admin/sms', icon: MessageSquare },
    { name: 'Revenue Reports', href: '/admin/reports', icon: BarChart3 },
    { name: 'Settings', href: '/admin/settings', icon: Settings },
  ];

  const handleLogout = () => {
    logout();
    window.location.href = '/';
  };

  const isActive = (href) => {
    if (href === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-stone-900/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-64 bg-stone-800 transform transition-transform duration-300
        lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-20 px-5 border-b border-stone-700">
            <Link to="/admin" className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_photo-gallery-orders/artifacts/59pp4syw_BEP%20Logo%202017-1.png" 
                alt="Bright Eyes Photography"
                className="h-10 w-auto brightness-0 invert"
              />
            </Link>
            <button 
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-stone-400 hover:text-white"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-6">
            <nav className="px-3 space-y-1">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-medium transition-colors
                    ${isActive(item.href) 
                      ? 'bg-amber-700 text-white' 
                      : 'text-stone-300 hover:bg-stone-700 hover:text-white'}
                  `}
                  data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon className="h-5 w-5" />
                  {item.name}
                </Link>
              ))}
            </nav>
          </ScrollArea>

          {/* User info */}
          <div className="p-4 border-t border-stone-700">
            <div className="flex items-center gap-3">
              <div className="bg-stone-700 rounded-full p-2">
                <User className="h-5 w-5 text-stone-300" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name || 'Lisa'}</p>
                <p className="text-xs text-stone-400 truncate">{user?.email}</p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Header */}
        <header className="sticky top-0 z-30 glass-header">
          <div className="flex items-center justify-between h-16 px-4 lg:px-8">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-stone-600 hover:text-stone-900"
              data-testid="mobile-menu-btn"
            >
              <Menu className="h-6 w-6" />
            </button>

            <div className="flex-1 lg:flex-none" />

            <div className="flex items-center gap-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                    <div className="bg-stone-200 rounded-full p-1.5">
                      <User className="h-4 w-4 text-stone-600" />
                    </div>
                    <span className="hidden sm:inline text-sm font-medium">{user?.name || 'Lisa'}</span>
                    <ChevronDown className="h-4 w-4 text-stone-400" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-stone-500">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-menu-item">
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
